import { HttpInterceptorFn } from "@angular/common/http";
import { inject } from "@angular/core";
import { CHATBOT_CONFIG } from "../config";
import { TEST_AUTHORIZATION_HEADER } from "../models/domain";
import { IdentityService } from "../services/identity.service";

/**
 * Attaches the caller's domain and credentials to requests aimed at the chatbot API, and to
 * nothing else.
 *
 * The backend answers 401 to a request that does not say which domain it is, so this interceptor
 * is what makes the chatbot work at all. For the anonymous domain it also carries the visitor's
 * own UUID, which is that domain's entire identity; the others send none, because their bearer
 * token already carries theirs.
 *
 * Two rules decide whether a request is allowed to carry credentials, and both fail closed. A
 * request that satisfies neither is forwarded untouched, which is a 401 from the backend rather
 * than a silent leak.
 *
 * **It must be addressed to the configured API.** An embedding host shares its HttpClient with
 * this library, so every call that host makes to its own backend, its asset servers and any third
 * party passes through here. Matching on the API base URL, rather than on "looks like an absolute
 * URL", is what keeps a live session token from being handed to hosts that have no business
 * seeing it.
 *
 * **It must travel over TLS.** An identity header and a bearer token are credentials, and in an
 * airworthiness context they never belong on an unencrypted connection. Plain HTTP is accepted
 * only when the host explicitly opts in through the configuration's `allowInsecureTransport`,
 * which it must wire to the flag that marks its local development build and nothing else.
 */
export const identityInterceptor: HttpInterceptorFn = (req, next) => {
    const identity = inject(IdentityService);
    const config = inject(CHATBOT_CONFIG);

    if (!targetsChatbotApi(req.url, config.apiUrl, config.allowInsecureTransport ?? false)) {
        return next(req);
    }

    const setHeaders: Record<string, string> = identity.identityHeaders();
    const token = identity.bearerToken();
    if (token) {
        setHeaders["Authorization"] = `Bearer ${token}`;
    }

    // Two credentials travel only while a test identity is held, and they answer different
    // questions: the bearer token above is the user the agent is answering, this one is the
    // tester who asked for an undeployed configuration to be run.
    const testAuthorization = identity.testAuthorizationToken();
    if (testAuthorization) {
        setHeaders[TEST_AUTHORIZATION_HEADER] = `Bearer ${testAuthorization}`;
    }

    if (Object.keys(setHeaders).length === 0) {
        return next(req);
    }

    return next(req.clone({ setHeaders }));
};

/**
 * Whether this URL is the configured chatbot API, reached over an acceptable transport.
 *
 * Both sides are resolved to absolute URLs before comparing, so a relative request and a
 * configured base written with or without a trailing slash compare the same way. The path check
 * uses a segment boundary, so an API based at `/api/v1` never matches a sibling like
 * `/api/v1public`.
 */
export function targetsChatbotApi(requestUrl: string, apiUrl: string, allowInsecureTransport: boolean): boolean {
    if (!apiUrl) {
        return false;
    }

    let target: URL;
    let api: URL;
    try {
        const base = typeof window === "undefined" ? undefined : window.location.href;
        target = new URL(requestUrl, base);
        api = new URL(apiUrl, base);
    } catch {
        return false;
    }

    if (target.protocol !== "https:" && !(allowInsecureTransport && target.protocol === "http:")) {
        return false;
    }

    if (target.origin !== api.origin) {
        return false;
    }

    const apiPath = api.pathname.replace(/\/+$/, "");
    return apiPath === "" || target.pathname === apiPath || target.pathname.startsWith(`${apiPath}/`);
}
